import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useTheme } from '../contexts/ThemeContext';

export interface UiSelectOption {
  value: string;
  label: string;
}

interface UiSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: UiSelectOption[];
  placeholder?: string;
  /** extra classes for the wrapper (e.g. width) */
  className?: string;
  disabled?: boolean;
}

/**
 * Modern, theme-aware dropdown. Replaces native <select> so the open menu is a
 * styled floating panel (rounded card, gradient-highlighted selected row with a
 * check, hover tint) instead of the browser's default option list.
 */
export const UiSelect: React.FC<UiSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select',
  className,
  disabled,
}) => {
  const { currentTheme } = useTheme();
  const c = currentTheme.colors;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} className={clsx('relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 rounded-xl border px-3.5 py-2.5 text-sm font-medium transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          backgroundColor: c.background.secondary,
          borderColor: open ? c.primary[400] : c.border.primary,
          color: selected ? c.text.primary : c.text.secondary,
          boxShadow: open ? `0 0 0 4px color-mix(in srgb, ${c.primary[500]} 18%, transparent)` : 'none',
        }}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown
          className={clsx('h-4 w-4 shrink-0 transition-transform duration-200', open && 'rotate-180')}
          style={{ color: c.text.secondary }}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 right-0 z-50 mt-2 rounded-2xl border p-1.5 max-h-72 overflow-auto"
          style={{
            backgroundColor: c.background.card,
            borderColor: c.border.primary,
            boxShadow: '0 24px 60px -18px rgba(16,24,40,0.45)',
          }}
        >
          {options.map(o => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className="w-full flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm font-medium text-left transition-colors"
                style={
                  active
                    ? { background: `linear-gradient(135deg, ${c.primary[500]}, ${c.primary[700]})`, color: '#fff' }
                    : { color: c.text.primary, backgroundColor: 'transparent' }
                }
                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = c.primary[50]; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span className="truncate">{o.label}</span>
                {active && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
